import { useCallback, useEffect, useRef, useState } from 'react'
import { useTheme } from '@mui/material'
import posthog from 'posthog-js'
import { useParams } from 'react-router-dom'
import useMergedData from '../hooks/useMergedData'
import useComputeNodes, { useComputeNodesLoader } from '../hooks/useComputeNodes'
import useWindowSize from '../hooks/useWindowSize'
import useGraphRouter from '../hooks/useGraphRouter'
import GraphNetwork from '../graph/core'
import MapNetwork from '../graph/mapCore'
import useGraphView from '../models/useGraphView'
import useApp from '../models/useApp'
import useAuth from '../models/useAuth'
import useSearch from '../models/useSearch'
import useNode from '../models/useNode'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { getGraphStyles } from '../graph/styles'
import { allowRebuild, drawingMissing } from '../graph/recovery'
import GraphFailed from './GraphFailed'
import useSetPlaces from '../models/useSetPlaces'
import useOrganizations from '../models/useOrganizations'
import { presumeController } from '../utilities/nodeAccess'
import { MoveNodesDialog, ResetPlaceDialog, type NodesMove, type PlaceReset } from './PlaceDialogs'

type GraphProps = {
  /** Space taken at the bottom of the screen by something drawn over the canvas: the tab bar of
   * the app on a phone (PhoneShell.tsx). The notes about the map are kept above it. */
  bottomInset?: string
}

export default function Graph({ bottomInset }: Readonly<GraphProps> = {}) {
  const { palette, typography } = useTheme()
  const { accountId, networkId, nodeId } = useParams()
  const [activeId, setActiveId] = useState<string>()
  const { width, height } = useWindowSize()
  const { ready, loaded, refreshedAt } = useApp()
  const svgRef = useRef<SVGSVGElement | null>(null)
  // The graph or the map (nobgp-router#1934). Both engines take the same `update` and emit the
  // same events, so everything below drives either one. Changing the view builds a new engine.
  const mapElement = useRef<HTMLDivElement | null>(null)
  const graphRef = useRef<GraphNetwork | MapNetwork | null>(null)
  const mapView = useGraphView(s => s.mapView)
  const setMapView = useGraphView(s => s.setMapView)
  const viewer = useGraphView(s => s.viewer)
  const loadViewer = useGraphView(s => s.loadViewer)
  const selfId = useAuth(s => s.user?.id)
  // The org whose nodes the stores hold: it changes when the nodes of a new org arrive.
  const loadedOrgId = useNode(s => s.loadedOrgId)
  const dark = palette.mode === 'dark'
  // How many nodes the map cannot draw: they have no place, and the router has none for the
  // viewer either. The map says so, because a node that is silently absent reads as a node
  // that is gone.
  const [unplaced, setUnplaced] = useState(0)
  // The map's tiles do not load, or the map library did not load (nobgp/app#449). The page says
  // so: a map with no tiles is an empty page, and a switch that goes back to the graph with no
  // word reads as a broken switch.
  const [tilesFailed, setTilesFailed] = useState(false)
  const [libraryFailed, setLibraryFailed] = useState(false)
  const router = useGraphRouter()
  const merged = useMergedData()
  // The graph is mounted on every app page, so it is the one place that loads the `tasks` rows
  // the compute marks read (app#329). Other surfaces call `useComputeNodes` only.
  useComputeNodesLoader()
  const compute = useComputeNodes()
  const data = { ...merged, compute }
  const query = useSearch(s => s.query)

  // ⚠⚠ THE PLACES A PERSON SETS ON THE MAP (nobgp-router#1976). A drag of a location or of the home
  // icon moves its nodes to where they really are, when the address places them wrong (Benoit's
  // Starlink nodes, placed at the exit point). A drag of a cloud location puts it where every member
  // sees it. A right-click on a placed one puts it back. The router decides who may: the node's
  // controllers, and an Owner or Admin for a cloud; the map offers the drag only to them.
  const setPlaces = useSetPlaces(s => s.places)
  const cloudPlaces = useSetPlaces(s => s.clouds)
  const organizations = useOrganizations(s => s.organizations)
  const orgRole = useOrganizations(s => s.role)
  const allNodes = useNode(s => s.nodes)
  const [move, setMove] = useState<NodesMove | null>(null)
  const [reset, setReset] = useState<PlaceReset | null>(null)
  const [placeError, setPlaceError] = useState<string | null>(null)
  const refusedText = (refused: string[], total: number) =>
    refused.length === total
      ? 'The place could not be stored. Try again.'
      : `${refused.length} of ${total} nodes could not be moved. Try again, or ask an Admin of their organization.`
  // The note is about one drop of a cloud, so it goes after a while: it stayed for the life of the page.
  useEffect(() => {
    if (!placeError) return
    const timer = setTimeout(() => setPlaceError(null), 10_000)
    return () => clearTimeout(timer)
  }, [placeError])

  const handleClick = (d: GraphNode) => router(d)
  const handleEnter = (activeId: string) => setActiveId(activeId)
  const handleLeave = () => setActiveId(undefined)
  const handleClose = () => router()

  /**
   * Build a new graph instance: bump `generation`, which the build effect depends on. Its cleanup
   * destroys the old instance first.
   *
   * ⚠⚠ BOUNDED BY `allowRebuild` (app#349). Past the budget the graph stops and shows
   * `GraphFailed`, because a draw that fails on the data held now fails again on the same data.
   */
  const [generation, setGeneration] = useState(0)
  const [failed, setFailed] = useState(false)
  const rebuilds = useRef<number[]>([])
  // ⚠ One rebuild per ask. `pageshow` and `visibilitychange` BOTH fire on a bfcache restore, so a
  // single wake used to spend two budget units for one recovery — three wakes in a minute then
  // landed on `GraphFailed` although every one of them was legitimate. Cleared by the build
  // effect below, which is what actually answers the ask.
  const rebuilding = useRef(false)
  const rebuild = useCallback((reason: string, error?: unknown) => {
    if (rebuilding.current) return
    const message = error instanceof Error ? error.message : error ? String(error) : undefined
    console.error(`graph: rebuilding (${reason})`, error ?? '')
    const next = allowRebuild(rebuilds.current, Date.now())
    rebuilds.current = next.history
    posthog.capture('graph rebuild', { reason, message, allowed: next.allowed })
    if (next.allowed) {
      // ⚠⚠ CLEAR `failed`. It used to be a one-way latch that only "Draw again" opened: once the
      // budget was spent, a later rebuild that the slid window allowed drew a working graph with
      // "The network graph could not be drawn" still on top of it, for the life of the tab.
      rebuilding.current = true
      setFailed(false)
      setGeneration(g => g + 1)
    } else setFailed(true)
  }, [])
  const redraw = () => {
    rebuilds.current = []
    rebuilding.current = true
    setFailed(false)
    setGeneration(g => g + 1)
  }

  useEffect(() => {
    if (!ready || !loaded || !svgRef.current || graphRef.current) return
    rebuilding.current = false
    if (mapView && mapElement.current) {
      const map = new MapNetwork({ svg: svgRef.current, mapElement: mapElement.current, width, height, dark })
      // ⚠ A browser with no WebGL cannot open the map, and it fails the same way every time. Go
      // back to the graph and do not build again: `error` would spend the rebuild budget on it.
      // ⚠⚠ ONLY THAT CASE STORES THE CHOICE OFF (`browser`). When the map LIBRARY did not load (a
      // tab from before a deploy asks for a chunk that is gone), go back to the graph for this
      // page and keep the stored choice: a reload opens the map.
      map.on('unavailable', (error: unknown, browser: boolean) => {
        if (browser) {
          console.error('the map view is not available in this browser', error)
          setMapView(false)
        } else {
          console.error('the map library did not load; reload the page to open the map', error)
          useGraphView.setState({ mapView: false })
          setLibraryFailed(true)
        }
      })
      map.on('unplaced', setUnplaced)
      map.on('trouble', setTilesFailed)
      map.on('moveNodes', (next: NodesMove) => setMove(next))
      map.on('resetNodes', (next: PlaceReset) => setReset(next))
      map.on('resetCloud', (next: { cloud: CloudPlace }) => setReset({ cloud: next.cloud, label: next.cloud === 'grok' ? 'Grok Bot' : 'noBGP cloud' }))
      map.on('moveCloud', ({ id, cloud, place }: { id: string; cloud: CloudPlace; place: { lat: number; lng: number } }) => {
        setPlaceError(null)
        void useSetPlaces
          .getState()
          .placeCloud(cloud, place)
          .catch((error: unknown) => {
            console.warn('map: the cloud place could not be stored', error)
            setPlaceError('The place of the cloud could not be stored. It is back where the map puts it.')
          })
          .finally(() => map.release(id))
      })
      setLibraryFailed(false)
      graphRef.current = map
    } else {
      graphRef.current = new GraphNetwork({ svg: svgRef.current, width, height })
    }
    graphRef.current.on('click', handleClick)
    graphRef.current.on('enter', handleEnter)
    graphRef.current.on('leave', handleLeave)
    graphRef.current.on('close', handleClose)
    graphRef.current.on('error', error => rebuild('draw failed', error))
    return () => {
      graphRef.current?.destroy()
      graphRef.current = null
      setUnplaced(0)
      setTilesFailed(false)
    }
    // `rebuild` is stable (useCallback with no deps); the handlers above are read at build time,
    // as they always were. ⚠ `mapView` is here so that the switch builds the other engine; `dark`
    // is not, because the map changes its style in place (below).
  }, [ready, loaded, generation, rebuild, mapView])

  // The viewer's place, asked for once, the first time the map is shown. The map draws the user
  // icon there, and a node that has no place of its own.
  useEffect(() => {
    if (mapView) void loadViewer()
  }, [mapView, loadViewer])
  useEffect(() => {
    if (graphRef.current instanceof MapNetwork) graphRef.current.setViewer(viewer ?? null)
  }, [viewer, mapView, generation, ready, loaded])
  // The viewer's own account is the home icon, when the fleet has accounts of other members too.
  useEffect(() => {
    if (graphRef.current instanceof MapNetwork) graphRef.current.setSelf(selfId)
  }, [selfId, mapView, generation, ready, loaded])
  // ⚠ Before the update effect below, so that the draw of the new org's nodes fits the map.
  useEffect(() => {
    if (graphRef.current instanceof MapNetwork) graphRef.current.setOrg(loadedOrgId)
  }, [loadedOrgId, mapView, generation, ready, loaded])
  useEffect(() => {
    if (graphRef.current instanceof MapNetwork) graphRef.current.setDark(dark)
  }, [dark, mapView, generation, ready, loaded])
  useEffect(() => {
    if (!(graphRef.current instanceof MapNetwork)) return
    const byId = new Map(allNodes.map(node => [node.id, node]))
    graphRef.current.setMoveRules({
      nodes: ids => ids.every(id => {
        const node = byId.get(id)
        return !!node && presumeController(organizations, node, selfId)
      }),
      clouds: orgRole === 'owner' || orgRole === 'admin',
    })
  }, [allNodes, organizations, selfId, orgRole, mapView, generation, ready, loaded])
  useEffect(() => {
    if (graphRef.current instanceof MapNetwork) graphRef.current.setPlaces(cloudPlaces, new Set(setPlaces.map(place => place.nodeId)))
  }, [cloudPlaces, setPlaces, mapView, generation, ready, loaded])

  /**
   * Check the drawing when the tab is shown again and when the app finishes a data refresh.
   *
   * ⚠ A tab left alone, or a computer that slept, comes back through `visibilitychange` and
   * `pageshow`, and the app's wake refresh ends with a new `refreshedAt`. Each of these is a moment
   * the reader looks at the graph, so each asks whether it is still drawn, rebuilds it when it is
   * not, and otherwise catches up with a window size that changed while the tab was hidden and
   * may not have reached `useWindowSize`.
   */
  const svgHealth = useCallback(() => {
    if (!ready || !loaded) return
    const svg = svgRef.current
    const hasData = merged.nodes.length > 0 || merged.networks.length > 0 || merged.accounts.length > 0
    if (drawingMissing(hasData, !!graphRef.current, !!svg?.querySelector('g.zoom'))) {
      rebuild('drawing missing')
      return
    }
    // ⚠ Only when the size really changed: `updateSize` re-fits a frame the reader has not zoomed,
    // and moving the picture on a tab switch alone is the jump `core.ts` works to avoid.
    //
    // ⚠⚠ THROUGH `useWindowSize`, NOT STRAIGHT TO `updateSize`. `width`/`height` are also the
    // `<svg>` element's own attributes below, so calling `updateSize(innerWidth, …)` here re-fits
    // the content to a size the canvas does not have: resized 1200 → 1800 while hidden, the graph
    // re-centres on x = 900 inside an `<svg>` still 1200 wide and its right half is drawn off the
    // canvas. Dispatching `resize` sets the hook's state, the `<svg>` re-renders at the new size,
    // and the `[width, height]` effect below calls `updateSize`. The guard is unchanged, so a
    // plain tab switch still moves nothing.
    if (window.innerWidth !== width || window.innerHeight !== height) {
      window.dispatchEvent(new Event('resize'))
    }
  }, [ready, loaded, merged.nodes.length, merged.networks.length, merged.accounts.length, rebuild, width, height])

  /**
   * Moves each time the tab is shown, so the update effect below draws once to catch up with the
   * data that arrived while it was hidden and skipped its draws.
   */
  const [shownAt, setShownAt] = useState(0)

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      svgHealth()
      setShownAt(Date.now())
    }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('pageshow', onVisible)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('pageshow', onVisible)
    }
  }, [svgHealth])

  useEffect(() => {
    if (refreshedAt) svgHealth()
    // Only a NEW refresh asks; `svgHealth` changing identity with the data must not.
  }, [refreshedAt])

  useEffect(() => {
    // ⚠ `failed` stops here too. `data` is a fresh literal every render, so this effect runs on
    // every render; with the old instance still mounted its DOM handlers keep firing, and a mouse
    // moving over the stale drawing drove one throw, one `console.error` and one PostHog event
    // per pointer move, indefinitely.
    if (!graphRef.current || failed) return
    // ⚠ No draw while the tab is hidden: nothing is seen, and d3 cannot run a transition without
    // an animation frame, so each draw only queued work for the moment the tab came back (the
    // idle-tab hang; `core.ts` bounds that queue on its own for a covered window that still
    // reports itself visible). `shownAt` brings the one catch-up draw.
    if (document.visibilityState === 'hidden') return
    graphRef.current.update(
      data,
      activeId,
      accountId ?? nodeId ?? networkId,
      // ⚠ A node's services show when it is pointed at or selected, and never all of them: there
      // is no switch any more (Benoit, 2026-10-02: "this is useless"). See `graph/leaves.ts`.
      query
    )
  }, [activeId, data, accountId, nodeId, networkId, query, generation, failed, shownAt])

  useEffect(() => {
    if (!graphRef.current) return
    graphRef.current.updateSize(width, height)
  }, [width, height])

  const notes = [
    placeError,
    libraryFailed && !mapView ? 'The map did not load. Reload the page, then open the map again.' : null,
    mapView && tilesFailed ? 'The map tiles did not load. The nodes are at their places.' : null,
    mapView && unplaced === 1 ? '1 node has no location and is not on the map' : null,
    mapView && unplaced > 1 ? `${unplaced} nodes have no location and are not on the map` : null,
  ].filter((note): note is string => !!note)

  return (
    <Box sx={getGraphStyles({ palette, typography })}>
      {/* The map draws under the `<svg>`, which then takes no pointer events of its own, so a
          drag or a wheel on an empty part reaches the map (`styles.ts`, `.map .point`). */}
      {mapView && <div ref={mapElement} style={{ position: 'fixed', inset: 0 }} />}
      <svg
        ref={svgRef}
        width={width}
        height={height}
        style={{ position: 'fixed', ...(mapView ? { pointerEvents: 'none' } : { cursor: 'grab' }) }}
      />
      {/* What the reader must know about the map, in one place: bottom centre, above the credits
          and clear of the drawer on the left and of the zoom buttons on the right. */}
      {notes.length > 0 && (
        <Stack
          spacing={0.5}
          sx={{
            position: 'fixed',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: bottomInset ? `calc(44px + ${bottomInset})` : 44,
            alignItems: 'center',
            maxWidth: 'calc(100vw - 32px)',
            pointerEvents: 'none',
          }}>
          {notes.map(note => (
            <Typography
              key={note}
              variant="caption"
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: 1,
                textAlign: 'center',
                color: 'text.secondary',
                backgroundColor: 'background.paper',
              }}>
              {note}
            </Typography>
          ))}
        </Stack>
      )}
      {failed && <GraphFailed onRedraw={redraw} />}
      {move && (
        <MoveNodesDialog
          key={move.id}
          move={move}
          onMoveTo={point => (graphRef.current instanceof MapNetwork ? graphRef.current.moveTo(move.id, point) : undefined)}
          onCancel={() => {
            if (graphRef.current instanceof MapNetwork) graphRef.current.release(move.id)
            setMove(null)
          }}
          onSave={async place => {
            const refused = await useSetPlaces.getState().setNodes(move.nodeIds, place)
            if (refused.length) return refusedText(refused, move.nodeIds.length)
            if (graphRef.current instanceof MapNetwork) graphRef.current.release(move.id)
            setMove(null)
            return null
          }}
        />
      )}
      {reset && (
        <ResetPlaceDialog
          reset={reset}
          onCancel={() => setReset(null)}
          onConfirm={async () => {
            try {
              if ('cloud' in reset) await useSetPlaces.getState().clearCloud(reset.cloud)
              else {
                const refused = await useSetPlaces.getState().clearNodes(reset.nodeIds)
                if (refused.length) return refusedText(refused, reset.nodeIds.length)
              }
            } catch {
              return 'It could not be put back. Try again.'
            }
            setReset(null)
            return null
          }}
        />
      )}
    </Box>
  )
}
